/* eslint-disable no-unused-vars */
import { Platform } from 'react-native';
import { EnvVars } from '../constants/EnvConfig';

let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

const FgRed = '\x1b[31m';
const FgYellow = '\x1b[33m';
const FgWhite = '\x1b[37m';

const getPrefix = () => `${new Date().toISOString()} [ptf]`;

function prefix(message) {
  return `${getPrefix()} ${message}`;
}

/**
 * Logger class with dependency injection for Sentry and config
 */
export class Logger {
  /**
   * Creates a logger instance
   * @param {typeof Sentry} sentry - Sentry instance
   * @param {typeof EnvVars} config - Environment configuration
   */
  constructor(sentry, config) {
    this.sentry = sentry;
    this.config = config;
  }

  /**
   * Logs an error to Sentry and/or the console, depending on the environment.
   * If the error is instance of an `Error` (or subclass), Sentry is sent
   * the error's _enumerable_ properties as context
   * @param {unknown} error - The error to log
   * @param {unknown} details - Optional additional details to include in Sentry context
   * @returns {string | null} Event ID produced by Sentry
   */
  error(error, details = undefined) {
    if (this.config.consoleLog) {
      // eslint-disable-next-line no-console
      console.log(FgRed, getPrefix(), error, details);
    }

    if (!this.config.useSentry) {
      return null;
    }

    try {
      // see: https://docs.sentry.io/platforms/javascript/usage/#capturing-errors
      const errorDetail = error instanceof Error ? { ...error, ...details } : details ?? {};
      return this.sentry.captureException(error, { contexts: { errorDetail } });
    } catch (logErrorError) {
      // eslint-disable-next-line no-console
      console.error(prefix('An error could not be reported to Sentry'), { error: logErrorError });
      return null;
    }
  }

  /**
   * Logs a message with optional details to the console
   * @param {string} message - The message to log
   * @param {any} details - Optional details to include
   */
  info(message, details = undefined) {
    const formattedMessage = prefix(message);
    if (this.config.consoleLog) {
      if (details !== undefined) {
        // eslint-disable-next-line no-console
        console.log(FgWhite, formattedMessage, details);
      } else {
        // eslint-disable-next-line no-console
        console.log(FgWhite, formattedMessage);
      }
    }
  }

  /**
   * Logs a debug message to the console
   * @param {string} message - The message to log
   */
  debug(message) {
    const formattedMessage = prefix(message);
    if (this.config.debugLog) {
      // eslint-disable-next-line no-console
      console.log(FgYellow, formattedMessage);
    }
  }
}

// Default singleton instance for application use
export const logger = new Logger(Sentry, EnvVars);

// Convenience exports for backward compatibility
// Bind methods to preserve 'this' context when called standalone
export const logError = logger.error.bind(logger);
export const logInfo = logger.info.bind(logger);
export const log = logger.info.bind(logger);
export const debug = logger.debug.bind(logger);
